import { createEffect, createMemo } from 'solid-js';
import { usePayPaymentMethods, usePortalPaymentAccounts, useCurrentCompany } from '~/contexts/global';
import { mode, PaymentMode } from '~/hooks/usePaymentMode';
import { PaymentMethodPurpose } from '~/swagger/pay/Api';
import { filterActivePaymentMethods } from '~/utils/receivingAccountsByMode';
import type { Accessor } from 'solid-js';
import type { PaymentMethodDto } from '~/swagger/pay/Api';

type PortalContext = ReturnType<typeof usePortalPaymentAccounts>;
type PayContext = ReturnType<typeof usePayPaymentMethods>;

type PortalCompanyPaymentAccounts = ReturnType<PortalContext['paymentAccounts']>;
type PortalValidatedPaymentAccounts = ReturnType<PortalContext['validatedPaymentAccounts']>;

type PayCompanyMethods = ReturnType<PayContext['methods']>;

export type UnifiedPaymentAccounts =
  | {
      mode: PaymentMode.Portal;
      paymentAccounts: PortalCompanyPaymentAccounts | undefined;
      validatedPaymentAccounts: PortalValidatedPaymentAccounts | undefined;
      hasPaymentMethod: boolean;
      isLoading: boolean;
    }
  | {
      mode: PaymentMode.Pay;
      paymentAccounts: PayCompanyMethods | undefined;
      validatedPaymentAccounts: PaymentMethodDto[] | undefined;
      hasPaymentMethod: boolean;
      isLoading: boolean;
    };

export const useCompanyPaymentAccountsByMode = (): Accessor<UnifiedPaymentAccounts> => {
  const { currentCompany } = useCurrentCompany();

  const portal = mode() === PaymentMode.Portal ? usePortalPaymentAccounts() : null;
  const pay = mode() === PaymentMode.Pay ? usePayPaymentMethods() : null;

  const companyId = createMemo(() => currentCompany()?.id);

  createEffect(() => {
    if (mode() !== PaymentMode.Pay || !pay) return;
    if (companyId() != null) {
      pay.setMethodsFilter({ deactivated: false });
    }
  });

  const portalPaymentAccounts = createMemo<PortalCompanyPaymentAccounts | undefined>(() => {
    if (mode() !== PaymentMode.Portal || !portal) return undefined;
    return portal.paymentAccounts();
  });

  const portalValidatedPaymentAccounts = createMemo<PortalValidatedPaymentAccounts | undefined>(() => {
    if (mode() !== PaymentMode.Portal || !portal) return undefined;
    return portal.validatedPaymentAccounts();
  });

  const payMethods = createMemo<PayCompanyMethods | undefined>(() => {
    if (mode() !== PaymentMode.Pay || !pay) return undefined;
    return pay.methods();
  });

  const payValidatedPaymentAccounts = createMemo<PaymentMethodDto[] | undefined>(() => {
    if (mode() !== PaymentMode.Pay || !pay) return undefined;
    const items = pay.methods()?.items ?? [];
    const active = filterActivePaymentMethods(items);
    return active.filter((m) => m.capabilities?.includes(PaymentMethodPurpose.Collect));
  });

  const hasPaymentMethod = createMemo<boolean>(() => {
    if (mode() === PaymentMode.Portal) {
      const list = portalValidatedPaymentAccounts();
      return !!list && list.length > 0;
    }
    if (mode() === PaymentMode.Pay) {
      const list = payValidatedPaymentAccounts();
      return list != null && list.length > 0;
    }
    return false;
  });

  const isLoading = createMemo<boolean>(() => {
    if (mode() === PaymentMode.Portal && portal) {
      return portal.paymentAccounts.loading;
    }
    if (mode() === PaymentMode.Pay && pay) {
      return pay.methods.loading;
    }
    return false;
  });

  const portalData = createMemo<UnifiedPaymentAccounts>(() => {
    return {
      mode: PaymentMode.Portal,
      paymentAccounts: portalPaymentAccounts(),
      validatedPaymentAccounts: portalValidatedPaymentAccounts(),
      hasPaymentMethod: hasPaymentMethod(),
      isLoading: isLoading(),
    };
  });

  const payData = createMemo<UnifiedPaymentAccounts>(() => {
    return {
      mode: PaymentMode.Pay,
      paymentAccounts: payMethods(),
      validatedPaymentAccounts: payValidatedPaymentAccounts(),
      hasPaymentMethod: hasPaymentMethod(),
      isLoading: isLoading(),
    };
  });

  return mode() === PaymentMode.Portal ? portalData : payData;
};
